<template>
  <PageHeader
    breadcrumb
    :items="[{ label: customerName, to: '/agreement/' + route.params.id }, { label: $t('ui.actions.edit') }]"
  ></PageHeader>

  <div v-if="agreementApiRequest.isLoading.value || state == null">
    <div class="grid sm:grid-cols-1 md:grid-cols-4 gap-x-8">
      <Skeleton
        width="100%"
        height="14rem"
      />
      <div class="md:col-span-3">
        <Skeleton
          width="100%"
          height="80vh"
        />
      </div>
    </div>
  </div>

  <div
    v-else
    class="grid sm:grid-cols-1 md:grid-cols-8 gap-x-8"
  >
    <div class="md:col-span-2">
      <Tabs
        v-model="currentTab"
        :tabs="tabs"
        direction="vertical"
        type="pills-rail-block"
        class="mb-8 item"
      />
    </div>
    <div
      v-if="currentTab == 'basic'"
      class="md:col-span-6"
    >
      <Card>
        <CardBody class="max-w-[600px]">
          <Form>
            <div class="grid md:grid-cols-3 md:gap-x-4">
              <FormGroup
                :label="$t('ui.entities.profile.companyName')"
                class="md:col-span-3"
                :is-required="v$.basic.companyName.required"
              >
                <Input
                  v-model="state.basic.companyName"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.basic.companyName.$error"
                />
                <InputError :has-error="v$.basic.companyName.$error">
                  {{ v$.basic.companyName.$errors[0]?.$message }}
                </InputError>
              </FormGroup>

              <FormGroup
                :label="$t('ui.common.title')"
                class="w-full"
              >
                <Select
                  v-model="state.basic.title"
                  mode="single"
                  :can-clear="true"
                  :options="titleOptions"
                  handle-search
                  class="w-full"
                  :readonly="!$can('edit_agreement_details')"
                ></Select>
              </FormGroup>
              <FormGroup
                :label="$t('ui.entities.profile.firstName')"
                class="w-full"
                :is-required="v$.basic.forename.required"
              >
                <Input
                  v-model="state.basic.forename"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.basic.forename.$error"
                />
                <InputError :has-error="v$.basic.forename.$error">
                  {{ v$.basic.forename.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup
                :label="$t('ui.entities.profile.lastName')"
                class="w-full"
                :is-required="v$.basic.surname.required"
              >
                <Input
                  v-model="state.basic.surname"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.basic.surname.$error"
                />
                <InputError :has-error="v$.basic.surname.$error">
                  {{ v$.basic.surname.$errors[0]?.$message }}
                </InputError>
              </FormGroup>

              <FormGroup :label="$t('ui.entities.profile.dateOfBirth')">
                <DatePicker
                  v-model="state.basic.dateOfBirth"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.basic.dateOfBirth.$error"
                />
                <InputError :has-error="v$.basic.dateOfBirth.$error">
                  {{ v$.basic.dateOfBirth.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
            </div>
            <Button
              :disabled="!$can('edit_agreement_details')"
              label="Save Changes"
              class="mt-4"
              :is-loading="submitApiRequest.isLoading.value"
              @click="submitCustomerDetails('basic')"
            />
          </Form>
        </CardBody>
      </Card>
    </div>

    <div
      v-if="currentTab == 'address'"
      class="md:col-span-6"
    >
      <Card>
        <CardBody>
          <Form>
            <div class="max-w-[600px] grid md:grid-cols-2 md:gap-x-4">
              <FormGroup
                :label="$t('ui.entities.address.line1')"
                class="md:col-span-2"
                :is-required="v$.address.line1.required"
              >
                <Input
                  v-model="state.address.line1"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.address.line1.$error"
                />
                <InputError :has-error="v$.address.line1.$error">
                  {{ v$.address.line1.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup
                :label="$t('ui.entities.address.line2')"
                class="md:col-span-2"
              >
                <Input
                  v-model="state.address.line2"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.address.line2.$error"
                />
                <InputError :has-error="v$.address.line2.$error">
                  {{ v$.address.line2.$errors[0]?.$message }}
                </InputError>
              </FormGroup>

              <FormGroup
                class="w-full"
                :label="$t('ui.entities.address.city')"
                :is-required="v$.address.town.required"
              >
                <Input
                  v-model="state.address.town"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.address.town.$error"
                />
                <InputError :has-error="v$.address.town.$error">{{ v$.address.town.$errors[0]?.$message }}</InputError>
              </FormGroup>
              <FormGroup
                class="w-full"
                :label="$t('ui.entities.address.county')"
                :is-required="v$.address.county.required"
              >
                <Input
                  v-model="state.address.county"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.address.county.$error"
                />
                <InputError :has-error="v$.address.county.$error">
                  {{ v$.address.county.$errors[0]?.$message }}
                </InputError>
              </FormGroup>

              <FormGroup
                :label="$t('ui.entities.address.postCode')"
                class="w-full"
                :is-required="v$.address.postcode.required"
              >
                <Input
                  v-model="state.address.postcode"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.address.postcode.$error"
                />
                <InputError :has-error="v$.address.postcode.$error">
                  {{ v$.address.postcode.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup
                :label="$t('ui.entities.address.country')"
                class="w-full"
                :is-required="v$.address.country.required"
              >
                <Select
                  v-model="state.address.country"
                  mode="single"
                  :close-on-select="true"
                  searchable
                  handle-search
                  :options="countriesList"
                  label-key="name"
                  value-key="code"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.address.country.$error"
                >
                  <template #value="{ value }">
                    <div class="flex items-center">
                      <img
                        :src="value.image"
                        width="25"
                        class="mr-3"
                      />
                      {{ value.name }}
                    </div>
                  </template>
                  <template #option="{ option }">
                    <div class="flex items-center">
                      <img
                        :src="option.image"
                        width="25"
                        class="mr-3"
                      />
                      {{ option.name }}
                    </div>
                  </template>
                </Select>
                <InputError :has-error="v$.address.country.$error">
                  {{ v$.address.country.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
            </div>

            <Button
              :disabled="!$can('edit_agreement_details')"
              label="Save Changes"
              class="mt-4"
              :is-loading="submitApiRequest.isLoading.value"
              @click="submitCustomerDetails('address')"
            />
          </Form>
        </CardBody>
      </Card>
    </div>

    <div
      v-if="currentTab == 'contact-methods'"
      class="md:col-span-6"
    >
      <Card>
        <CardBody>
          <Form>
            <div class="grid md:grid-cols-2 gap-4">
              <div class="max-w-[400px]">
                <CardTitle :title="$t('ui.entities.comms.phoneNumber', 2)" />
                <FormGroup label="Mobile">
                  <InputPhone
                    v-model="state.contactMethods.mobile"
                    :readonly="!$can('edit_customer_contact_methods')"
                  />
                </FormGroup>
                <FormGroup label="Telephone 1">
                  <InputPhone
                    v-model="state.contactMethods.telephone1"
                    :readonly="!$can('edit_customer_contact_methods')"
                  />
                </FormGroup>
                <FormGroup label="Telephone 2">
                  <InputPhone
                    v-model="state.contactMethods.telephone2"
                    :readonly="!$can('edit_customer_contact_methods')"
                  />
                </FormGroup>
              </div>

              <div class="max-w-[400px]">
                <CardTitle :title="$t('ui.entities.comms.emailAddress')" />
                <FormGroup>
                  <Input
                    v-model="state.contactMethods.email"
                    icon="at"
                    :readonly="!$can('edit_customer_contact_methods')"
                    :has-error="v$.contactMethods.email.$error"
                  />
                  <InputError :has-error="v$.contactMethods.email.$error">
                    {{ v$.contactMethods.email.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
              </div>
            </div>

            <Button
              :disabled="!$can('edit_customer_contact_methods')"
              label="Save Changes"
              class="mt-4"
              :is-loading="submitApiRequest.isLoading.value"
              @click="submitCustomerDetails('contact-methods')"
            />
          </Form>
        </CardBody>
      </Card>
      <Card class="md:max-w-[350px]">
        <CardBody>
          <CardTitle
            title="Imported Contact Methods"
            class="text-secondary"
          />
          <FormGroup
            label="Mobile"
            class="text-secondary"
          >
            <span>{{ state.contactMethods.importedContactMethods?.mobile ?? '-' }}</span>
          </FormGroup>
          <FormGroup
            label="Tel 1"
            class="text-secondary"
          >
            <span>{{ state.contactMethods.importedContactMethods?.telephone1 ?? '-' }}</span>
          </FormGroup>
          <FormGroup
            label="Tel 2"
            class="text-secondary"
          >
            <span>{{ state.contactMethods.importedContactMethods?.telephone2 ?? '-' }}</span>
          </FormGroup>
          <FormGroup
            label="Email"
            class="text-secondary"
          >
            <span>{{ state.contactMethods.importedContactMethods?.email ?? '-' }}</span>
          </FormGroup>
        </CardBody>
      </Card>
    </div>

    <div
      v-if="currentTab == 'vehicle'"
      class="col-span-6"
    >
      <Card>
        <CardBody>
          <Form>
            <div class="max-w-[600px]">
              <FormGroup
                :label="$t('ui.entities.vehicle.vrm')"
                class="mb-6"
                :is-required="v$.vehicle.vrm.required"
              >
                <div class="grid md:grid-cols-3 md:gap-x-4">
                  <Input
                    v-model="state.vehicle.vrm"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.vehicle.vrm.$error"
                  />
                  <Button
                    label="Get Vehicle Details"
                    outlined
                    :disabled="
                      !$can('edit_agreement_details') || !state.vehicle.vrm || dvlaEnquiryRequest.isLoading.value
                    "
                    :is-loading="dvlaEnquiryRequest.isLoading.value"
                    @click="fetchVehicleDetailsFromDVLA"
                  />
                </div>
                <InputError :has-error="v$.vehicle.vrm.$error">{{ v$.vehicle.vrm.$errors[0]?.$message }}</InputError>
              </FormGroup>

              <div class="grid md:grid-cols-3 md:gap-x-4">
                <FormGroup
                  :label="$t('ui.entities.vehicle.make')"
                  :is-required="v$.vehicle.make.required"
                >
                  <Select
                    v-model="state.vehicle.make"
                    mode="single"
                    :close-on-select="true"
                    handle-search
                    :options="manufacturers"
                    on-select-return-as-object
                    label-key="name"
                    searchable
                    value-key="name"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.vehicle.make.$error"
                  >
                    <template #value="{ value }">
                      <div class="flex items-center">
                        <img
                          :src="value.squareLogoUrl"
                          style="width: 20px; max-height: 20px"
                          class="mr-3"
                        />
                        {{ value.name }}
                      </div>
                    </template>
                    <template #option="{ option }">
                      <div class="flex items-center">
                        <img
                          :src="option.squareLogoUrl"
                          style="width: 20px; max-height: 20px"
                          class="mr-3"
                        />
                        {{ option.name }}
                      </div>
                    </template>
                  </Select>
                  <InputError :has-error="v$.vehicle.make.$error">
                    {{ v$.vehicle.make.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.vehicle.model')"
                  :is-required="v$.vehicle.model.required"
                >
                  <Select
                    v-model="state.vehicle.model"
                    mode="single"
                    :close-on-select="true"
                    searchable
                    handle-search
                    :options="vehicleModels.data.value || []"
                    :has-error="v$.vehicle.model.$error"
                    :readonly="!$can('edit_agreement_details') || !state.vehicle.make"
                    label-key="model"
                    value-key="model"
                  />
                  <InputError :has-error="v$.vehicle.model.$error">
                    {{ v$.vehicle.model.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.vehicle.derivative')"
                  :is-required="v$.vehicle.derivative.required"
                >
                  <Input
                    v-model="state.vehicle.derivative"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.vehicle.derivative.$error"
                  />
                  <InputError :has-error="v$.vehicle.derivative.$error">
                    {{ v$.vehicle.derivative.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
              </div>

              <div class="grid md:grid-cols-3 md:gap-x-4">
                <FormGroup label="Fuel Type">
                  <Select
                    v-model="state.vehicle.fuelType"
                    mode="single"
                    :close-on-select="true"
                    searchable
                    :options="fuelTypeOptions"
                    label-key="label"
                    value-key="value"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.vehicle.fuelType.$error"
                  />
                  <InputError :has-error="v$.vehicle.fuelType.$error">
                    {{ v$.vehicle.fuelType.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup label="Color">
                  <Select
                    v-model="state.vehicle.color"
                    mode="single"
                    :close-on-select="true"
                    searchable
                    handle-search
                    :options="colorOptions"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.vehicle.color.$error"
                  />
                  <InputError :has-error="v$.vehicle.color.$error">
                    {{ v$.vehicle.color.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup label="Doors">
                  <RadioTabbed
                    v-model="state.vehicle.doors"
                    :options="doorsOptions"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.vehicle.doors.$error"
                  />
                  <InputError :has-error="v$.vehicle.doors.$error">
                    {{ v$.vehicle.doors.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
              </div>

              <div class="grid md:grid-cols-3 md:gap-x-4">
                <FormGroup
                  :label="$t('ui.forms.modelYear')"
                  :is-required="v$.vehicle.modelYear.required"
                >
                  <Input
                    v-model="state.vehicle.modelYear"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.vehicle.modelYear.$error"
                    :placeholder="$t('ui.forms.modelYearPlaceholder')"
                  />
                  <InputError :has-error="v$.vehicle.modelYear.$error">
                    {{ v$.vehicle.modelYear.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.vehicle.regDate')"
                  :is-required="v$.vehicle.regDate.required"
                >
                  <DatePicker
                    v-model="state.vehicle.regDate"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.vehicle.regDate.$error"
                  />
                  <InputError :has-error="v$.vehicle.regDate.$error">
                    {{ v$.vehicle.regDate.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
                <FormGroup
                  :label="$t('ui.entities.vehicle.newUsed')"
                  :disabled="true"
                >
                  <RadioTabbed
                    v-model="state.vehicle.newUsed"
                    :options="[
                      { label: 'New', value: 'New' },
                      { label: 'Used', value: 'Used' }
                    ]"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.vehicle.newUsed.$error"
                  />
                  <InputError :has-error="v$.vehicle.newUsed.$error">
                    {{ v$.vehicle.newUsed.$errors[0]?.$message }}
                  </InputError>
                </FormGroup>
              </div>

              <div class="grid md:grid-cols-3 md:gap-x-4">
                <FormGroup
                  :label="$t('ui.entities.vehicle.vin')"
                  class="md:col-span-2"
                  :is-required="v$.vehicle.vin.required"
                >
                  <Input
                    v-model="state.vehicle.vin"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.vehicle.vin.$error"
                  />
                  <InputError :has-error="v$.vehicle.vin.$error">{{ v$.vehicle.vin.$errors[0]?.$message }}</InputError>
                </FormGroup>
              </div>
            </div>
            <Button
              :disabled="!$can('edit_agreement_details')"
              label="Save Changes"
              class="mt-4"
              :is-loading="submitApiRequest.isLoading.value"
              @click="submitAgreementDetails('vehicle')"
            />
          </Form>
        </CardBody>
      </Card>
    </div>

    <div
      v-if="currentTab == 'agreement'"
      class="col-span-6"
    >
      <Card>
        <CardBody>
          <Form>
            <div class="grid md:grid-cols-3 md:gap-x-4 max-w-[600px]">
              <FormGroup
                :label="$t('ui.entities.financeAgreement.financeProduct')"
                :is-required="v$.agreement.productType.required"
              >
                <Select
                  v-model="state.agreement.productType"
                  mode="single"
                  searchable
                  :options="['PCP', 'HP', 'CH', 'BCH']"
                  class="w-full"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.agreement.productType.$error"
                />
                <InputError :has-error="v$.agreement.productType.$error">
                  {{ v$.agreement.productType.$errors[0]?.$message }}
                </InputError>
              </FormGroup>

              <FormGroup
                :label="$t('ui.entities.financeAgreement.financeProductName')"
                class="md:col-span-2"
                :is-required="v$.agreement.productName.required"
              >
                <Input
                  v-model="state.agreement.productName"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.agreement.productName.$error"
                />
                <InputError :has-error="v$.agreement.productName.$error">
                  {{ v$.agreement.productName.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup
                :label="$t('ui.entities.financeAgreement.term')"
                :is-required="v$.agreement.term.required"
              >
                <InputGroup>
                  <Input
                    v-model="state.agreement.term"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.agreement.term.$error"
                  />
                  <InputGroupAddon :readonly="!$can('edit_agreement_details')">months</InputGroupAddon>
                </InputGroup>
                <InputError :has-error="v$.agreement.term.$error">
                  {{ v$.agreement.term.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup
                :label="$t('ui.entities.financeAgreement.startDate')"
                :is-required="v$.agreement.startDate.required"
              >
                <DatePicker
                  v-model="state.agreement.startDate"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.agreement.startDate.$error"
                />
                <InputError :has-error="v$.agreement.startDate.$error">
                  {{ v$.agreement.startDate.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup
                :label="$t('ui.entities.financeAgreement.endDate')"
                :is-required="v$.agreement.endDate.required"
              >
                <DatePicker
                  v-model="state.agreement.endDate"
                  :readonly="!$can('edit_agreement_details')"
                  :has-error="v$.agreement.endDate.$error"
                />
                <InputError :has-error="v$.agreement.endDate.$error">
                  {{ v$.agreement.endDate.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup
                :label="$t('ui.entities.financeAgreement.purchasePrice')"
                :is-required="v$.agreement.purchasePrice.required"
              >
                <InputGroup>
                  <Input
                    v-model="state.agreement.purchasePrice"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.agreement.purchasePrice.$error"
                  />
                  <InputGroupAddon :readonly="!$can('edit_agreement_details')">
                    {{ state.agreement.currency }}
                  </InputGroupAddon>
                </InputGroup>
                <InputError :has-error="v$.agreement.purchasePrice.$error">
                  {{ v$.agreement.purchasePrice.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup :label="$t('ui.entities.financeAgreement.amountFinanced')">
                <InputGroup>
                  <Input
                    v-model="state.agreement.amountBorrowed"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.agreement.amountBorrowed.$error"
                  />
                  <InputGroupAddon :readonly="!$can('edit_agreement_details')">
                    {{ state.agreement.currency }}
                  </InputGroupAddon>
                </InputGroup>
                <InputError :has-error="v$.agreement.amountBorrowed.$error">
                  {{ v$.agreement.amountBorrowed.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup
                :label="$t('ui.entities.financeAgreement.deposit')"
                :is-required="v$.agreement.deposit.required"
              >
                <InputGroup>
                  <Input
                    v-model="state.agreement.deposit"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.agreement.deposit.$error"
                  />
                  <InputGroupAddon :readonly="!$can('edit_agreement_details')">
                    {{ state.agreement.currency }}
                  </InputGroupAddon>
                </InputGroup>
                <InputError :has-error="v$.agreement.deposit.$error">
                  {{ v$.agreement.deposit.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup :label="$t('ui.entities.financeAgreement.equity')">
                <InputGroup>
                  <Input
                    v-model="state.agreement.equity"
                    :readonly="!$can('edit_agreement_details')"
                  />
                  <InputGroupAddon :readonly="!$can('edit_agreement_details')">
                    {{ state.agreement.currency }}
                  </InputGroupAddon>
                </InputGroup>
              </FormGroup>
              <FormGroup
                :label="$t('ui.entities.financeAgreement.finalRepayment')"
                :is-required="v$.agreement.finalRepayment.required"
              >
                <InputGroup>
                  <Input
                    v-model="state.agreement.finalRepayment"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.agreement.finalRepayment.$error"
                  />
                  <InputGroupAddon :readonly="!$can('edit_agreement_details')">
                    {{ state.agreement.currency }}
                  </InputGroupAddon>
                </InputGroup>
                <InputError :has-error="v$.agreement.finalRepayment.$error">
                  {{ v$.agreement.finalRepayment.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup
                label="Rate"
                :is-required="v$.agreement.rate.required"
              >
                <InputGroup>
                  <Input
                    v-model="state.agreement.rate"
                    class="w-full"
                    icon="percent"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.agreement.rate.$error"
                  />
                  <InputGroupAddon :readonly="!$can('edit_agreement_details')">APR</InputGroupAddon>
                </InputGroup>
                <InputError :has-error="v$.agreement.rate.$error">
                  {{ v$.agreement.rate.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup
                :label="$t('ui.entities.financeAgreement.monthlyPayment')"
                :is-required="v$.agreement.monthlyRepayment.required"
              >
                <InputGroup>
                  <Input
                    v-model="state.agreement.monthlyRepayment"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.agreement.monthlyRepayment.$error"
                  />
                  <InputGroupAddon :readonly="!$can('edit_agreement_details')">
                    {{ state.agreement.currency }}
                  </InputGroupAddon>
                </InputGroup>
                <InputError :has-error="v$.agreement.monthlyRepayment.$error">
                  {{ v$.agreement.monthlyRepayment.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
              <FormGroup :label="$t('ui.entities.financeAgreement.annualDistanceAllowance')">
                <InputGroup>
                  <Input
                    v-model="state.agreement.odometerAllowance"
                    class="w-full"
                    icon="gauge"
                    :readonly="!$can('edit_agreement_details')"
                    :has-error="v$.agreement.odometerAllowance.$error"
                  />
                  <Select
                    v-model="state.agreement.odometerUnit"
                    mode="single"
                    :options="[
                      { label: 'mi', value: 'miles' },
                      { label: 'km', value: 'kilometers' }
                    ]"
                    label-key="label"
                    value-key="value"
                    class="w-auto"
                    :readonly="!$can('edit_agreement_details')"
                  />
                </InputGroup>
                <InputError :has-error="v$.agreement.odometerAllowance.$error">
                  {{ v$.agreement.odometerAllowance.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
            </div>
            <Button
              :disabled="!$can('edit_agreement_details')"
              label="Save Changes"
              class="mt-4"
              :is-loading="submitApiRequest.isLoading.value"
              @click="submitAgreementDetails('agreement')"
            />
          </Form>
        </CardBody>
      </Card>
    </div>
  </div>
</template>

<script setup>
import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'
import Tabs from '@/components/tabs/Tabs.vue'
import Form from '@/components/forms/Form.vue'
import InputGroup from '@/components/forms/InputGroup.vue'
import Input from '@/components/forms/Input.vue'
import InputPhone from '@/components/forms/InputPhone.vue'
import Button from '@/components/button/Button.vue'
import DatePicker from '@/components/forms/DatePicker.vue'
import RadioTabbed from '@/components/forms/RadioTabbed.vue'

import InputError from '@/components/forms/InputError.vue'

import useApiRequest from '@/composables/useApiRequest'

import Skeleton from '@/components/skeleton/Skeleton.vue'

import { useI18n } from 'vue-i18n'

import axios from 'axios'
import dayjs from 'dayjs'

import { ref, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import FormGroup from '@/components/forms/FormGroup.vue'
import Select from '@/components/forms/Select.vue'
import InputGroupAddon from '@/components/forms/InputGroupAddon.vue'

import { alertToast } from '@/utilities/notification'

import useVuelidate from '@vuelidate/core'
import { email, required, requiredIf, helpers, decimal, integer } from '@vuelidate/validators'

const { t } = useI18n()

const route = useRoute()
const router = useRouter()
const currentTab = ref(route.params.tab)

const tabs = ref([
  {
    id: 'basic',
    label: t('ui.common.basicInfo'),
    icon: 'user',
    to: '/agreement-edit/' + route.params.id
  },
  {
    id: 'address',
    label: t('ui.entities.address.label'),
    icon: 'location-dot',
    to: '/agreement-edit/' + route.params.id + '/address'
  },
  {
    id: 'contact-methods',
    label: t('ui.common.contactMethod', 2),
    icon: 'phone',
    to: '/agreement-edit/' + route.params.id + '/contact-methods'
  },
  {
    id: 'vehicle',
    label: t('ui.entities.vehicle.label'),
    icon: 'car',
    to: '/agreement-edit/' + route.params.id + '/vehicle'
  },
  {
    id: 'agreement',
    label: t('ui.entities.agreement.label'),
    icon: 'file',
    to: '/agreement-edit/' + route.params.id + '/agreement'
  }
])

// Set tab to 'Basic' if no tab is set
if (route.params.tab === '') {
  currentTab.value = 'basic'
} else {
  currentTab.value = route.params.tab
}

const titleOptions = ref(['Mr', 'Ms', 'Mrs', 'Miss', 'Dr'])

const fuelTypeOptions = ref([
  { label: 'Electricity', value: 'ELECTRICITY' },
  { label: 'Petrol', value: 'PETROL' },
  { label: 'Diesel', value: 'DIESEL' },
  { label: 'Hybrid Electric', value: 'HYBRID ELECTRIC' }
])

const doorsOptions = ref([
  { label: '2dr', value: 2 },
  { label: '5dr', value: 5 }
])

const colorOptions = ref([
  'Black',
  'White',
  'Silver',
  'Grey',
  'Red',
  'Blue',
  'Green',
  'Yellow',
  'Orange',
  'Brown',
  'Beige',
  'Gold',
  'Purple',
  'Pink',
  'Bronze',
  'Copper'
])

const countriesList = ref(null)

axios
  .get('https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/index.json', {
    withCredentials: false
  })
  .then(response => {
    const sortedCountries = response.data.sort((a, b) => {
      if (a.name < b.name) return -1
      if (a.name > b.name) return 1
      return 0
    })
    countriesList.value = sortedCountries
  })

const manufacturers = ref(null)
const manufacturersRequest = useApiRequest()
const vehicleModels = useApiRequest()
const dvlaEnquiryRequest = useApiRequest()

function fetchManufacturers() {
  manufacturersRequest.send({ endpoint: '/v1/manufacturers?showDisabled=true' }).then(response => {
    if (response.success) {
      manufacturers.value = response.data
    }
  })
}
fetchManufacturers()

const agreementApiRequest = useApiRequest()
const state = ref(null)

function fetchAgreementData() {
  agreementApiRequest.send({ endpoint: '/v1/agreements/' + route.params.id, method: 'GET' }).then(response => {
    if (response.success) {
      state.value = {
        basic: {
          id: response.data.customer.id,
          companyName: response.data.customer.companyName,
          title: response.data.customer.title,
          forename: response.data.customer.forename,
          surname: response.data.customer.surname,
          dateOfBirth: response.data.customer.dateOfBirth
        },
        address: {
          line1: response.data.customer.address.line1,
          line2: response.data.customer.address.line2,
          town: response.data.customer.address.town,
          county: response.data.customer.address.county,
          postcode: response.data.customer.address.postcode,
          country: response.data.customer.address.country
        },
        contactMethods: {
          mobile: response.data.customer.contact.mobile,
          telephone1: response.data.customer.contact.telephone1,
          telephone2: response.data.customer.contact.telephone2,
          email: response.data.customer.contact.email,
          importedContactMethods: response.data.customer.importedContactMethods
        },
        vehicle: {
          vrm: response.data.vehicle.vrm,
          vin: response.data.vehicle.vin,
          // Convert make string to object if manufacturers are loaded, otherwise keep as string
          make:
            typeof response.data.vehicle.make === 'string' && manufacturers.value
              ? manufacturers.value.find(m => m.name === response.data.vehicle.make) || response.data.vehicle.make
              : response.data.vehicle.make,
          model: response.data.vehicle.model,
          derivative: response.data.vehicle.derivative,
          newUsed: response.data.newUsed,
          regDate: response.data.vehicle.regDate,
          modelYear: response.data.vehicle.modelYear,
          fuelType: response.data.vehicle.fuelType,
          doors: response.data.vehicle.doors,
          color: response.data.vehicle.color
        },
        agreement: {
          productType: response.data.productType,
          productName: response.data.productName,
          term: response.data.term,
          startDate: response.data.startDate,
          endDate: response.data.endDate,
          purchasePrice: response.data.purchasePrice,
          amountBorrowed: response.data.amountBorrowed,
          deposit: response.data.deposit,
          equity: response.data.equity,
          finalRepayment: response.data.finalRepayment,
          rate: response.data.rate,
          monthlyRepayment: response.data.monthlyRepayment,
          odometerAllowance: response.data.odometer.allowanceYearly,
          odometerUnit: response.data.odometer.unit,
          currency: response.data.currency
        }
      }

      // Add current title to titleOptions if not already in the list
      if (state.value.basic.title !== null) {
        if (!titleOptions.value.includes(state.value.basic.title)) {
          titleOptions.value.push(state.value.basic.title)
        }
      }

      // Convert make string to object if manufacturers are loaded
      if (state.value.vehicle.make && typeof state.value.vehicle.make === 'string' && manufacturers.value) {
        const manufacturer = manufacturers.value.find(m => m.name === state.value.vehicle.make)
        if (manufacturer) {
          state.value.vehicle.make = manufacturer
        }
      }

      // Ensure color is in the options list
      ensureColorInList()

      // Fetch models if make exists
      if (state.value.vehicle.make) {
        const manufacturerId =
          typeof state.value.vehicle.make === 'object' && state.value.vehicle.make.id
            ? state.value.vehicle.make.id
            : null
        const manufacturerName =
          typeof state.value.vehicle.make === 'object' && state.value.vehicle.make.name
            ? state.value.vehicle.make.name
            : state.value.vehicle.make

        if (manufacturerId) {
          // Set previous make ID to prevent reset on initial load
          previousMakeId.value = manufacturerId
          vehicleModels.send({
            endpoint: '/v1/vehicles/models',
            params: { manufacturerId }
          })
        } else if (manufacturerName && manufacturers.value) {
          const manufacturer = manufacturers.value.find(m => m.name === manufacturerName)
          if (manufacturer) {
            // Set previous make ID to prevent reset on initial load
            previousMakeId.value = manufacturer.id
            vehicleModels.send({
              endpoint: '/v1/vehicles/models',
              params: { manufacturerId: manufacturer.id }
            })
          }
        }
      }
    }
  })
}
fetchAgreementData()

/**
 * Ensures the current model value is in the vehicle models list
 * This prevents data loss for rare models not in our database
 */
function ensureModelInList() {
  if (vehicleModels.data.value && Array.isArray(vehicleModels.data.value) && state.value?.vehicle?.model) {
    const currentModel = state.value.vehicle.model
    // API returns models as an array of objects with a 'model' property
    const modelExists = vehicleModels.data.value.some(model => {
      return typeof model === 'object' && model?.model === currentModel
    })

    // If current model is not in the list, add it as an option
    if (!modelExists && currentModel) {
      vehicleModels.data.value = [...vehicleModels.data.value, { model: currentModel }]
    }
  }
}

/**
 * Ensures the current color value is in the color options list
 * This prevents data loss for custom colors not in our predefined list
 */
function ensureColorInList() {
  if (state.value?.vehicle?.color && colorOptions.value) {
    const currentColor = state.value.vehicle.color
    const colorExists = colorOptions.value.includes(currentColor)

    // If current color is not in the list, add it as an option
    if (!colorExists && currentColor) {
      colorOptions.value = [...colorOptions.value, currentColor]
    }
  }
}

watch(
  () => currentTab.value,
  newTab => {
    currentTab.value = newTab
    // fetch tab route
    const tab = tabs.value.find(tab => tab.id === newTab)
    if (tab) {
      router.replace(tab.to)
    }
  }
)

/**
 * Watch for manufacturers to load and convert make string to object if needed
 */
watch(
  () => manufacturers.value,
  () => {
    if (state.value?.vehicle?.make && typeof state.value.vehicle.make === 'string' && manufacturers.value) {
      const manufacturer = manufacturers.value.find(m => m.name === state.value.vehicle.make)
      if (manufacturer) {
        state.value.vehicle.make = manufacturer
      }
    }
  }
)

// Track previous make ID to detect actual changes (not just initial load)
const previousMakeId = ref(null)

/**
 * Watch for changes to the vehicle make and fetch models when it changes
 */
watch(
  () => state.value?.vehicle?.make,
  newMake => {
    // Get the make ID for comparison
    const newMakeId = typeof newMake === 'object' && newMake?.id ? newMake.id : null

    // Reset model and clear model options when make changes (but not on initial load when previousMakeId is null)
    if (previousMakeId.value !== null && newMakeId !== previousMakeId.value && state.value?.vehicle) {
      state.value.vehicle.model = null
      // Clear model options immediately
      vehicleModels.data.value = []
    }

    // Update previous make ID
    if (newMakeId) {
      previousMakeId.value = newMakeId
    } else {
      previousMakeId.value = null
    }

    // Fetch models if a make is selected
    if (newMake) {
      // Handle both object (from Select) and string (from API) formats
      const manufacturerId = typeof newMake === 'object' && newMake.id ? newMake.id : null
      const manufacturerName = typeof newMake === 'object' && newMake.name ? newMake.name : newMake

      if (manufacturerId) {
        vehicleModels.send({
          endpoint: '/v1/vehicles/models',
          params: { manufacturerId }
        })
      } else if (manufacturerName && manufacturers.value) {
        // If we have a string make, find the manufacturer object to get the ID
        const manufacturer = manufacturers.value.find(m => m.name === manufacturerName)
        if (manufacturer) {
          vehicleModels.send({
            endpoint: '/v1/vehicles/models',
            params: { manufacturerId: manufacturer.id }
          })
        }
      }
    } else {
      // Clear models if no make is selected
      vehicleModels.data.value = []
    }
  }
)

/**
 * Watch for vehicle models to load and ensure current model is in the list
 * This prevents data loss for rare models not in our database
 */
watch(
  () => vehicleModels.data.value,
  () => {
    ensureModelInList()
  }
)

/**
 * Watch for color changes and ensure it's in the options list
 */
watch(
  () => state.value?.vehicle?.color,
  () => {
    ensureColorInList()
  }
)

const validationRules = {
  basic: {
    companyName: {
      required: helpers.withMessage(
        'Company name is required if no first and last name',
        requiredIf(() => {
          return (
            !state.value?.basic?.forename ||
            !state.value?.basic?.surname ||
            state.value?.basic?.forename === '' ||
            state.value?.basic?.surname === ''
          )
        })
      )
    },
    title: {},
    forename: {
      required: helpers.withMessage(
        'First name is required if no company name',
        requiredIf(() => {
          return !state.value?.basic?.companyName || state.value?.basic?.companyName === ''
        })
      )
    },
    surname: {
      required: helpers.withMessage(
        'Last name is required if no company name',
        requiredIf(() => {
          return !state.value?.basic?.companyName || state.value?.basic?.companyName === ''
        })
      )
    },
    dateOfBirth: {}
  },
  address: {
    line1: { required },
    line2: {},
    town: { required },
    county: { required },
    postcode: { required },
    country: { required }
  },
  contactMethods: {
    mobile: {},
    telephone1: {},
    telephone2: {},
    email: { email }
  },
  vehicle: {
    vrm: {
      required
    },
    vin: {
      required
    },
    make: { required },
    model: { required },
    derivative: { required },
    modelYear: { required },
    regDate: { required },
    newUsed: { required },
    fuelType: {},
    doors: {},
    color: {}
  },
  agreement: {
    productType: { required },
    productName: { required },
    term: { required, integer },
    startDate: { required },
    endDate: { required },
    purchasePrice: { required, decimal },
    amountBorrowed: { required, decimal },
    deposit: { required, decimal },
    equity: {},
    finalRepayment: { required, decimal },
    rate: { required, decimal },
    monthlyRepayment: { required, decimal },
    odometerAllowance: {
      valid: helpers.withMessage('Annual distance allowed must be 0 or greater', value => {
        if (value === null || value === undefined || value === '') return true
        const numValue = Number(value)
        return !isNaN(numValue) && numValue >= 0 && Number.isInteger(numValue)
      })
    },
    odometerUnit: {}
  }
}

const v$ = useVuelidate(validationRules, state)

const submitApiRequest = useApiRequest()

/**
 * Fetches vehicle details from DVLA API based on registration number
 */
async function fetchVehicleDetailsFromDVLA() {
  if (!state.value?.vehicle?.vrm) {
    alertToast('Please enter a registration number', null, 'error')
    return
  }

  const registrationNumber = state.value.vehicle.vrm.trim().toUpperCase()

  // Clear all vehicle data fields before fetching new details
  // This prevents leftover data from a previous incorrect VRM
  state.value.vehicle.make = null
  state.value.vehicle.model = null
  state.value.vehicle.derivative = null
  state.value.vehicle.fuelType = null
  state.value.vehicle.color = null
  state.value.vehicle.doors = null
  state.value.vehicle.modelYear = null
  state.value.vehicle.regDate = null
  state.value.vehicle.newUsed = null
  state.value.vehicle.vin = null
  // Clear vehicle models list
  vehicleModels.data.value = []

  dvlaEnquiryRequest
    .send({
      endpoint: '/v1/dvla/enquiry',
      method: 'GET',
      params: { registrationNumber }
    })
    .then(response => {
      if (response.success && response.data) {
        const data = response.data

        // Populate available fields (excluding model and derivative as per requirements)
        if (data.make && manufacturers.value) {
          // Match manufacturer case-insensitively since DVLA returns uppercase
          const manufacturer = manufacturers.value.find(m => m.name.toUpperCase() === data.make.toUpperCase())
          if (manufacturer) {
            state.value.vehicle.make = manufacturer
            // Fetch models for the selected make
            vehicleModels.send({
              endpoint: '/v1/vehicles/models',
              params: { manufacturerId: manufacturer.id }
            })
          } else {
            // If no match found, try to find a partial match or use the string as-is
            state.value.vehicle.make = data.make
          }
        }

        // Handle color field (check both 'color' and 'colour' for API compatibility)
        const colorValue = (data.color || data.colour)?.trim()
        if (colorValue && colorOptions.value) {
          // Match color case-insensitively since DVLA returns capitalized
          const matchingColor = colorOptions.value.find(c => c.toUpperCase() === colorValue.toUpperCase())
          if (matchingColor) {
            state.value.vehicle.color = matchingColor
            console.info('Color matched:', matchingColor, 'from API value:', colorValue)
          } else {
            // If no match found, add it to the options list
            state.value.vehicle.color = colorValue
            ensureColorInList()
            console.info('Color added to options:', colorValue)
          }
        } else {
          console.info(
            'Color not set - colorValue:',
            colorValue,
            'colorOptions available:',
            !!colorOptions.value,
            'API data:',
            data
          )
        }

        if (data.fuelType) {
          // Map DVLA fuel type to our format if needed
          const fuelTypeMapping = {
            ELECTRIC: 'ELECTRICITY',
            HYBRID: 'HYBRID ELECTRIC'
          }
          state.value.vehicle.fuelType = fuelTypeMapping[data.fuelType] || data.fuelType
        }

        if (data.monthOfFirstRegistration) {
          // Convert month/year to YYYY-MM-DD format
          // DVLA returns format like "YYYY-MM" or might return full date
          let regDateValue = data.monthOfFirstRegistration

          // If it's in YYYY-MM format, use the first day of that month
          if (typeof regDateValue === 'string' && regDateValue.match(/^\d{4}-\d{2}$/)) {
            regDateValue = regDateValue + '-01'
          }

          // Parse and format to YYYY-MM-DD
          const parsedDate = dayjs(regDateValue)
          if (parsedDate.isValid()) {
            state.value.vehicle.regDate = parsedDate.format('YYYY-MM-DD')
          } else {
            // If parsing fails, try to extract date from various formats
            const fallbackDate = dayjs(regDateValue, ['YYYY-MM-DD', 'YYYY-MM', 'DD/MM/YYYY', 'MM/DD/YYYY'], true)
            if (fallbackDate.isValid()) {
              state.value.vehicle.regDate = fallbackDate.format('YYYY-MM-DD')
            }
          }
        }

        alertToast('Vehicle details retrieved successfully', null, 'success')
      } else {
        alertToast('Failed to retrieve vehicle details', response.message || 'Unknown error', 'error')
      }
    })
    .catch(error => {
      alertToast('Failed to retrieve vehicle details', error.message || 'Unknown error', 'error')
    })
}

async function submitAgreementDetails(form) {
  let formData = {}

  if (form == 'vehicle') {
    const isFormCorrect = await v$.value.vehicle.$validate()
    if (!isFormCorrect) {
      return
    }
    formData = {
      vrm: state.value.vehicle.vrm,
      vin: state.value.vehicle.vin,
      make: typeof state.value.vehicle.make === 'object' ? state.value.vehicle.make.name : state.value.vehicle.make,
      model: state.value.vehicle.model,
      derivative: state.value.vehicle.derivative,
      newUsed: state.value.vehicle.newUsed,
      regDate: state.value.vehicle.regDate == '' ? null : state.value.vehicle.regDate,
      modelYear: state.value.vehicle.modelYear,
      fuelType: state.value.vehicle.fuelType,
      doors: state.value.vehicle.doors,
      color: state.value.vehicle.color
    }
  }

  if (form == 'agreement') {
    const isFormCorrect = await v$.value.agreement.$validate()
    if (!isFormCorrect) {
      return
    }
    formData = {
      financeProductType: state.value.agreement.productType,
      financeProductName: state.value.agreement.productName,
      term: state.value.agreement.term,
      startDate: state.value.agreement.startDate == '' ? null : state.value.agreement.startDate,
      endDate: state.value.agreement.endDate == '' ? null : state.value.agreement.endDate,
      purchasePrice: state.value.agreement.purchasePrice,
      amountBorrowed: state.value.agreement.amountBorrowed,
      deposit: state.value.agreement.deposit,
      equity: state.value.agreement.equity,
      balloon: state.value.agreement.finalRepayment,
      rate: state.value.agreement.rate,
      monthlyRepayment: state.value.agreement.monthlyRepayment,
      odometerAllowance: state.value.agreement.odometerAllowance,
      odometerUnit: state.value.agreement.odometerUnit
    }
  }

  submitApiRequest
    .send({ endpoint: '/v1/agreements/' + route.params.id, method: 'PATCH', data: formData })
    .then(response => {
      if (response.success) {
        // fetchAgreementData()
        alertToast('Updated', null, 'success')
      } else {
        alertToast('Failed to update', response.message, 'error')
      }
    })
}

async function submitCustomerDetails(form) {
  let customerFormData = {}

  if (form == 'basic') {
    const isFormCorrect = await v$.value.basic.$validate()
    if (!isFormCorrect) {
      return
    }
    customerFormData = {
      companyName: state.value.basic.companyName,
      title: state.value.basic.title,
      forename: state.value.basic.forename,
      surname: state.value.basic.surname,
      dateOfBirth: state.value.basic.dateOfBirth
    }
  }
  if (form == 'address') {
    const isFormCorrect = await v$.value.address.$validate()
    if (!isFormCorrect) {
      return
    }
    customerFormData = {
      addressLine1: state.value.address.line1,
      addressLine2: state.value.address.line2,
      addressTown: state.value.address.town,
      addressCounty: state.value.address.county,
      addressPostcode: state.value.address.postcode,
      addressCountry: state.value.address.country
    }
  }
  if (form == 'contact-methods') {
    const isFormCorrect = await v$.value.contactMethods.$validate()
    if (!isFormCorrect) {
      return
    }

    customerFormData = {
      contactTelephone1:
        state.value.contactMethods.telephone1.length > 5 ? state.value.contactMethods.telephone1 : null,
      contactTelephone2:
        state.value.contactMethods.telephone2.length > 5 ? state.value.contactMethods.telephone2 : null,
      contactMobile: state.value.contactMethods.mobile ? state.value.contactMethods.mobile : null,
      contactEmail: state.value.contactMethods.email ? state.value.contactMethods.email : null
    }
  }

  submitApiRequest
    .send({
      endpoint: '/v1/customers/' + state.value.basic.id,
      method: 'PATCH',
      data: customerFormData
    })
    .then(response => {
      if (response.success) {
        // fetchAgreementData()
        alertToast('Details Updated', null, 'success')
      } else {
        alertToast('Failed to update', response.message, 'error')
      }
    })
}

const customerName = computed(() => {
  if (state.value == null) return '...'
  // If company name is set, return company name
  if (state?.value?.basic?.companyName) {
    return state.value.basic.companyName
  } else {
    return state?.value?.basic?.forename + ' ' + state?.value?.basic?.surname
  }
})
</script>

<style></style>
